import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, combineLatest, map, ReplaySubject, switchMap } from 'rxjs';

import { BreadcrumbComponent, Breadcrumb } from 'components/breadcrumb/breadcrumb.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AdvertiserService, CampaignService, GamePlanLayoutState } from 'services';

const CARD_DESCS: Record<number, string> = {
    1: 'Most space per product',
    2: 'Generous space per product',
    3: 'Balanced space per product',
    4: 'Feature the most products',
};

const CARD_INFO: Record<number, { title: string; desc: string }> = {
    1: { title: '1 Product Selected', desc: 'Includes the largest product image and the most available space for product descriptions and messaging.' },
    2: { title: '2 Products Selected', desc: 'Content space is shared across two products.' },
    3: { title: '3 Products Selected', desc: 'Content space is shared across three products.' },
    4: { title: '4 Products Selected', desc: 'Showcase the most products in a single listing, with content space shared across all four.' },
};

@Component({
    selector: 'app-game-plan-layout',
    imports: [CommonModule, BreadcrumbComponent, NgxSkeletonLoaderModule],
    templateUrl: './game-plan-layout.component.html',
    styleUrl: './game-plan-layout.component.scss'
})
export class GamePlanLayoutComponent implements OnInit {
    readonly cards = [1, 2, 3, 4];
    readonly cardDescs = CARD_DESCS;
    readonly cardInfo = CARD_INFO;

    protected breadcrumbs$!: Observable<Breadcrumb[]>;

    protected state: GamePlanLayoutState | null = null;
    protected loading = true;
    protected saving = false;

    protected selectedCount: number | null = null;
    protected showModal = false;
    protected pendingCount: number | null = null;

    private advertiserIdSubject = new ReplaySubject<number>(1);
    private campaignIdSubject = new ReplaySubject<number>(1);

    private resolvedAdvertiserId: number | null = null;
    private resolvedCampaignId: number | null = null;

    @Input()
    set advertiserId(value: number) {
        this.resolvedAdvertiserId = value;
        this.advertiserIdSubject.next(value);
    }

    @Input()
    set campaignId(value: number) {
        this.resolvedCampaignId = value;
        this.campaignIdSubject.next(value);
    }

    constructor(
        private router: Router,
        private advertiserService: AdvertiserService,
        private campaignService: CampaignService,
    ) {}

    ngOnInit(): void {
        this.breadcrumbs$ = combineLatest([
            this.advertiserIdSubject,
            this.campaignIdSubject,
        ]).pipe(
            switchMap(([advertiserId]) => this.advertiserService.getAdvertiser(advertiserId).pipe(
                map(advertiser => ([
                    { label: advertiser.name, url: `/advertiser/${advertiser.aid}/upcoming-campaigns` },
                    { label: 'Upcoming Campaigns', url: `/advertiser/${advertiser.aid}/upcoming-campaigns` },
                    { label: 'Campaign', url: '' },
                ]))
            ))
        );

        this.campaignIdSubject.subscribe(campaignId => {
            this.campaignService.getGamePlanLayoutState(campaignId).subscribe(state => {
                this.state = state;
                this.loading = false;
                if (state.currentProductCount >= 1 && state.currentProductCount <= 4) {
                    this.selectedCount = state.currentProductCount;
                }
            });
        });
    }

    onCardClick(count: number): void {
        const needsWarning =
            this.state?.hasNonEmptyProducts &&
            count !== this.state?.currentProductCount &&
            (this.state?.currentProductCount ?? 0) > 0;

        if (needsWarning) {
            this.pendingCount = count;
            this.showModal = true;
        } else {
            this.selectedCount = count;
        }
    }

    onModalCancel(): void {
        this.pendingCount = null;
        this.showModal = false;
    }

    onModalConfirm(): void {
        if (this.pendingCount !== null) {
            this.selectedCount = this.pendingCount;
        }
        this.pendingCount = null;
        this.showModal = false;
    }

    onContinue(): void {
        if (this.selectedCount === null || this.saving) {
            return;
        }
        if (this.resolvedCampaignId === null || this.resolvedAdvertiserId === null) {
            return;
        }

        this.saving = true;
        const campaignId = this.resolvedCampaignId;
        const advertiserId = this.resolvedAdvertiserId;

        this.campaignService.saveGamePlanLayout(campaignId, this.selectedCount).subscribe({
            next: () => {
                this.router.navigate([`/advertiser/${advertiserId}/upcoming-campaigns/campaign/${campaignId}/edit`]);
            },
            error: () => {
                this.saving = false;
            },
        });
    }

    productSlots(count: number): number[] {
        return Array(count).fill(0);
    }
}
